import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography
} from '@mui/material';
import { HttpService } from 'core/services/http';
import { performAppLogout } from 'core/auth/performAppLogout';
import { useAuth0 } from "@auth0/auth0-react";
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useThemeMode } from 'cms/core/context/ThemeContext';
import useProfile from 'features/feature_user/hooks/UseProfile';
import { nameToAvatarColor } from 'features/feature_job/utils/profileDisplay';

const EmployerHeader = ({ onMenuClick, drawerWidth }) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = HttpService.getUser?.() ?? null;
  const { mode, toggleTheme } = useThemeMode();
  const { isAuthenticated: isAuth0Authenticated, logout: auth0Logout } = useAuth0();
  
  const token = HttpService.getToken();
  
  // Load profile to get user image
  const { data: profile } = useProfile({ enabled: !!token });
  
  // Helper function to get profile image URL
  const getProfileImage = (image) => {
    if (!image) return undefined;
    if (image.includes('/api/uploaded_media/https')) {
      return image.replace('/api/uploaded_media/', '');
    }
    if (image.startsWith('http')) {
      return image;
    }
    if (image.startsWith('/')) {
      return HttpService.SiteBase + image;
    }
    return HttpService.SiteBase + '/' + image;
  };
  
  const firstName = profile?.first_name || user?.first_name || '';
  const lastName = profile?.last_name || user?.last_name || '';
  const profileImage = getProfileImage(profile?.image || user?.image);
  const initials = firstName?.[0]?.toUpperCase() || lastName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'E';
  const avatarColorSeed = `${firstName} ${lastName}`.trim() || user?.email || 'Employer';

  const handleLogout = () => {
    performAppLogout(queryClient, {
      isAuth0Authenticated,
      auth0Logout,
    });
  };

    const getPageTitle = () => {
        const path = location.pathname;
        if (path === '/employer/dashboard') return 'Dashboard';
        if (path === '/employer/my-company') return 'My Company';
        if (path === '/employer/jobs') return 'Jobs';
        if (path.startsWith('/employer/jobs/')) return 'Job Details';
        if (path === '/employer/applications') return 'Applications';
        if (path.startsWith('/employer/applications/')) return 'Application Details';
        if (path === '/employer/employees') return 'Employees';
        if (path === '/employer/salary-specification') return 'Salary Specifications';
        if (path.includes('/salary-specification/') && path.includes('/deductions')) return 'Deduction Contributions';
        if (path.includes('/salary-specification/') && path.includes('/salary-details')) return 'Salary Details';
        if (path.startsWith('/employer/salary-specification/')) return 'Salary Specification';
        if (path === '/employer/invoices') return 'Invoices';
        if (path.startsWith('/employer/invoices/')) return 'Invoice Details';
        if (path === '/employer/contracts') return 'Contracts';
        if (path.includes('/contracts/') && path.includes('/print')) return 'Print Contract';
        if (path.startsWith('/employer/contracts/')) return 'Contract Details';
        if (path === '/employer/reports') return 'Reports';
        return 'Employer Dashboard';
    };

  return (
    <AppBar
      position="fixed"
      color="inherit"
      elevation={0}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        width: { md: `calc(100% - ${drawerWidth ?? 0}px)` },
        ml: { md: `${drawerWidth ?? 0}px` }
      }}
    >
      <Toolbar sx={{ minHeight: 72, px: { xs: 2, md: 4 } }}>
        <IconButton
          edge="start"
          color="inherit"
          sx={{ mr: 2, display: { md: 'none' } }}
          onClick={onMenuClick}
        >
          <MenuIcon />
        </IconButton>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Welcome back
          </Typography>
          <Typography variant="h6" fontWeight={600}>
            {getPageTitle()}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip label="Employer" color="primary" variant="outlined" />
          <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton color="inherit" onClick={toggleTheme}>
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Sign out">
            <IconButton color="inherit" onClick={handleLogout}>
              <LogoutOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Avatar 
            src={profileImage}
            alt={firstName || 'User'}
            sx={{ bgcolor: nameToAvatarColor(avatarColorSeed), fontWeight: 700 }}
          >
            {initials}
          </Avatar>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default EmployerHeader;
